import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '@/lib/firebase';
import { API_BASE } from '@/lib/apiBase';

const AuthContext = createContext(null);
const API = API_BASE;

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

// Add token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Firebase Phone Auth: setup recaptcha
  const setupRecaptcha = (elementId) => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
        size: 'invisible',
        callback: () => {},
      });
    }
    return window.recaptchaVerifier;
  };

  // Errors we MUST surface to the user (don't fall back — user needs to fix input)
  const FIREBASE_USER_FIXABLE_CODES = new Set([
    'auth/invalid-phone-number',
    'auth/invalid-verification-code',
    'auth/code-expired',
    'auth/missing-verification-code',
    'auth/missing-phone-number',
    'auth/too-many-requests',
  ]);

  const shouldFallback = (err) => {
    if (!err) return false;
    // If it's an explicit user-fixable error, surface it (don't fall back)
    if (FIREBASE_USER_FIXABLE_CODES.has(err.code)) return false;
    // Everything else (network, recaptcha, referer block, quota, billing, axios "Request failed", reCAPTCHA dismissed, etc.) → fall back to backend OTP
    return true;
  };

  // Send OTP: try Firebase first, fall back to backend mock-OTP so the app
  // remains usable on preview/non-production domains.
  const sendOtp = async (phone, recaptchaElementId = 'recaptcha-container') => {
    try {
      const appVerifier = setupRecaptcha(recaptchaElementId);
      const confirmationResult = await signInWithPhoneNumber(auth, phone, appVerifier);
      window.confirmationResult = confirmationResult;
      window.__otpFallback = false;
      return { message: 'OTP sent via Firebase', provider: 'firebase' };
    } catch (err) {
      // Reset recaptcha so it can be re-initialised later
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch { /* noop */ }
        window.recaptchaVerifier = null;
      }
      if (!shouldFallback(err)) throw err;

      // Fallback: backend mock-OTP (returned in the response for demo)
      const { data } = await api.post('/auth/send-otp', { phone });
      window.__otpFallback = true;
      window.__mockOtp = data.mock_otp;
      return { message: 'OTP sent (demo mode)', provider: 'mock', mock_otp: data.mock_otp };
    }
  };

  // Verify OTP: route to the same provider that produced it.
  const verifyOtp = async (phone, code, name) => {
    // Any successful phone login clears the admin-only session flag
    localStorage.removeItem('adminOnly');
    if (window.__otpFallback) {
      const { data } = await api.post('/auth/verify-otp', { phone, code, name: name || undefined });
      if (data.token) localStorage.setItem('token', data.token);
      setUser(data.user);
      window.__otpFallback = false;
      window.__mockOtp = null;
      return data;
    }

    if (!window.confirmationResult) {
      // Defensive: no Firebase session in memory. Try backend mock OTP — this also handles
      // the case where the user refreshed between sending and verifying.
      try {
        const { data } = await api.post('/auth/verify-otp', { phone, code, name: name || undefined });
        if (data.token) localStorage.setItem('token', data.token);
        setUser(data.user);
        return data;
      } catch (e) {
        throw new Error('Please request OTP first');
      }
    }
    try {
      const result = await window.confirmationResult.confirm(code);
      const firebaseUser = result.user;
      const idToken = await firebaseUser.getIdToken();
      const { data } = await api.post('/auth/firebase-login', {
        firebase_token: idToken,
        name: name || undefined,
      });
      if (data.token) localStorage.setItem('token', data.token);
      setUser(data.user);
      window.confirmationResult = null;
      return data;
    } catch (err) {
      // If Firebase confirm fails for non-user-fixable reasons (e.g. backend Firebase verify error,
      // billing, quota), try backend mock OTP as a last resort.
      if (FIREBASE_USER_FIXABLE_CODES.has(err.code)) {
        throw err;
      }
      try {
        const { data } = await api.post('/auth/verify-otp', { phone, code, name: name || undefined });
        if (data.token) localStorage.setItem('token', data.token);
        setUser(data.user);
        return data;
      } catch {
        throw err; // re-throw original Firebase error if backend fallback also fails
      }
    }
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post('/auth/admin-login', { email, password });
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    // Persist session flag — email-based admin sessions cannot access the seller app
    localStorage.setItem('adminOnly', '1');
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      // Sign out of Firebase too
      await auth.signOut();
    } catch { /* noop */ }
    localStorage.removeItem('token');
    localStorage.removeItem('adminOnly');
    setUser(null);
  };

  const isAdminOnlySession = () => localStorage.getItem('adminOnly') === '1';

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, sendOtp, verifyOtp, adminLogin, logout, refreshUser, api, isAdminOnlySession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export { api };
